import {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
} from 'react';
import api from '../api/endpoints';
import { useAuth } from './AuthContext';
import { useToast } from './ToastContext';

const CartContext = createContext(null);

/**
 * Carts are kept per shop on the server (a customer can have an open cart with
 * several shops at once). This context mirrors them for the tab badge and the
 * quantity steppers on product tiles.
 */
export const CartProvider = ({ children }) => {
  const { user, mode } = useAuth();
  const toast = useToast();
  const [carts, setCarts] = useState([]);
  const [loading, setLoading] = useState(false);

  /**
   * Keyed on `mode` - the side of the app in use - not on `user.role`.
   *
   * The role is a one-way upgrade: anyone who has ever opened partner signup
   * keeps `role: 'partner'` forever. Gating on it meant those accounts silently
   * dropped every cart while shopping - "Add" reached the server fine, then
   * this blanked the local copy, so no badge and no stepper ever appeared.
   */
  const refresh = useCallback(async () => {
    if (!user || mode === 'partner') { setCarts([]); return; }
    setLoading(true);
    try {
      const res = await api.cart.list();
      setCarts(res.data || []);
    } catch (e) {
      setCarts([]);
    } finally {
      setLoading(false);
    }
  }, [user, mode]);

  useEffect(() => { refresh(); }, [refresh]);

  /**
   * Failures are reported here rather than at each call site - every page was
   * calling `.catch(() => {})`, so an out-of-stock or closed-shop error just
   * made the Add button look dead.
   */
  // `options` is the customisation: { variantLabel, addons: [{ group, name }], note }.
  const addItem = useCallback(async (productId, quantity = 1, options = {}) => {
    try {
      const res = await api.cart.add({ productId, quantity, ...options });
      await refresh();
      return res.data;
    } catch (e) {
      toast.error(e.message || 'Could not add to cart');
      throw e;
    }
  }, [refresh, toast]);

  const updateItem = useCallback(async (productId, quantity, lineKey) => {
    await api.cart.update({ productId, quantity, lineKey });
    await refresh();
  }, [refresh]);

  const clearShop = useCallback(async (partnerId) => {
    await api.cart.clear(partnerId);
    await refresh();
  }, [refresh]);

  /** How many of a given product are already in a cart, across every customised line. */
  const quantityOf = useCallback((productId) => carts.reduce((total, cart) => (
    (cart.items || []).reduce((n, i) => (
      String((i.product && i.product._id) || i.product) === String(productId)
        ? n + i.quantity
        : n
    ), total)
  ), 0), [carts]);

  const itemCount = useMemo(
    () => carts.reduce((n, c) => n + (c.items || []).reduce((q, i) => q + i.quantity, 0), 0),
    [carts],
  );

  const value = useMemo(() => ({
    carts, loading, refresh, addItem, updateItem, clearShop, quantityOf, itemCount,
  }), [carts, loading, refresh, addItem, updateItem, clearShop, quantityOf, itemCount]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => useContext(CartContext);
export default CartContext;
